import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactQuill, { Quill } from 'react-quill-new';
import styled from 'styled-components';
import 'react-quill/dist/quill.snow.css';

import { ProductParamInput } from 'containers/ProductRegist/hooks/types';
import HackleManager from 'utils/hackle';
import TitleContainer from '../TitleContainer';
import { useIntroduceModifyHooks } from 'containers/ProductDetail/components/ModifyForm/hooks/useIntroductModifyHooks';
import ImageUploadLoading from 'components/Loading/ImageUploadLoading';
import EditorFormat, {
  formats,
  toolbarContainer,
} from 'components/Editor/EditorFormat';

import QuillTableBetter from 'quill-table-better';
import 'react-quill-new/dist/quill.snow.css';
import 'quill-table-better/dist/quill-table-better.css';
import {
  handleTransformYouTubeVideoUrlByClick,
  handleTransformYouTubeVideoUrlByEnter,
  initEditorValue,
} from 'utils/editor';
import { normalizeTableTemporary } from 'utils/editorContent';
import GoTopButton from 'components/GoTopButton/goTopButton';
import useScrollCheck from 'hooks/useScrollCheck';
import { debounce } from 'lodash';

Quill.register({ 'modules/table-better': QuillTableBetter }, true);

const QuillContainer = styled.div<{ $isMobile: boolean }>`
  position: relative;
  width: 100%;
  overflow-y: scroll;

  .quill {
    padding: ${props => (props.$isMobile ? '12px 20px' : '12px 40px')};

    .ql-container {
      overflow-y: scroll;
      height: 100%;
    }

    .ql-tooltip.ql-editing {
      height: auto;
    }

    .ql-editor {
      overflow-y: scroll;
      padding: 12px 0px;
      border-top: 1px solid #eee;

      min-height: 200px;

      img {
        border-radius: 0px;
      }
    }

    ol {
      li {
        display: flex;
        align-items: center;

        &::before {
          content: '';
          height: 100%;
        }
      }
    }

    .ql-toolbar {
      padding: ${props => (props.$isMobile ? '0px 0 10px' : '24px 0')};
      top: 0;
      /* top: ${props => (props.$isMobile ? '0' : '100px')}; */
    }

    .ql-formats {
      margin-right: 0;

      > button {
        &.ql-table-better {
          width: 18px;
          height: 18px;

          svg {
            width: 18px !important;
            height: 18px !important;
          }
        }
      }
    }

    table {
      * {
        box-sizing: border-box;
      }

      &.ql-table-better {
      }

      tbody {
      }

      tr {
        -webkit-border-width: 1px;
        border-width: 1px !important;
      }

      td {
        display: table-cell;
        vertical-align: inherit;
        border: 1px solid #000;
        padding: 2px 5px;
        -webkit-border-width: 1px;
        border-width: 1px !important;
      }
    }
  }
`;

const GoTopButtonBox = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  position: absolute;
  right: 0px;
  bottom: 16px;
  z-index: 21;

  width: auto;
  padding: 0;

  pointer-events: none;

  ${({ $isMobile }) => `
      bottom:  ${$isMobile ? '101px' : '16px'};
      padding:  ${$isMobile ? '0px 20px' : '0px 40px'}
  `}
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  top: 0;
  padding: 2px 0;
  z-index: 21;

  input {
    display: none;
  }
`;

interface ChapterThreeIProps {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  innerHeight: number;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
}

const ChapterThree: React.FC<ChapterThreeIProps> = props => {
  const {
    isMobile,
    productParamInput,
    innerHeight,
    handleOnChangeProductParamInput,
  } = props;

  const params = useParams<{ id: string }>();

  const {
    containerRef,
    quillRef,
    fileInputRef,
    value,
    imageLoading,
    handleImageUploadClick,
    handleFileChange,
    handleImageDrop,
  } = useIntroduceModifyHooks(
    productParamInput.htmlDetailContent
      ? productParamInput.htmlDetailContent
      : '',
  );

  const [editorHeight, setEditorHeight] = useState<number>(innerHeight);
  const [isVisibleHeader, setIsVisibleHeader] = useState<boolean>(true);

  const handleResizeViewPort = (keyboardHeight: { keyboardHeight: string }) => {
    setIsVisibleHeader(false);

    setEditorHeight(innerHeight - Number(keyboardHeight.keyboardHeight));
  };

  const handleResizeOnBlur = () => {
    setIsVisibleHeader(true);

    setEditorHeight(innerHeight);
  };

  const imageHandler = (imageDataUrl, type, imageData) => {
    const file = imageData.toFile();

    handleImageDrop(file);
  };

  const handleGotoTop = (behavior: 'auto' | 'smooth') => {
    const quillElement = document.querySelector('.quill');
    const parent = quillElement.parentElement;

    if (quillElement && parent) {
      parent.scrollTo({ top: 0, behavior: behavior });
    }
  };

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: toolbarContainer,
        handlers: {
          image: () => {
            handleImageUploadClick();
          },
          divider: function () {
            // 현재 커서 위치 가져오기
            const range = this.quill.getSelection(true);
            // 구분선 삽입
            this.quill.insertEmbed(range.index, 'divider', true);
            // 커서 위치 업데이트
            this.quill.setSelection(range.index + 2, 0);
          },
        },
      },
      table: false,
      'table-better': {
        menus: [
          'column',
          'row',
          'merge',
          'table',
          'cell',
          'wrap',
          'copy',
          'delete',
        ],
        toolbarTable: true,
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
      imageDropAndPaste: {
        handler: imageHandler,
      },
    };
  }, []);

  useEffect(() => {
    handleOnChangeProductParamInput('htmlDetailContent', value);
  }, [value]);

  useEffect(() => {
    if (quillRef.current) {
      initEditorValue(quillRef, productParamInput.htmlDetailContent);

      const editor = quillRef.current.getEditor();

      // 이미지 근처 Enter 시 스크롤 점프 방지 (Quill 내부 스크롤 오버라이드)
      editor.scrollSelectionIntoView = () => {};

      editor.blur();
    }
  }, [quillRef]);

  useEffect(() => {
    window.handleResizeViewPort = handleResizeViewPort;
    window.handleResizeOnBlur = handleResizeOnBlur;
    // 컴포넌트 마운트 시 컨텐츠가 있는 경우 스크롤을 맨 아래로 이동
    if (productParamInput.htmlDetailContent && containerRef.current) {
      //   setTimeout(() => {
      //     if (containerRef.current) {
      //       containerRef.current.style.scrollBehavior = 'auto';
      //       containerRef.current.scrollTop = containerRef.current.scrollHeight;
      //       // 부드러운 스크롤 효과 복원
      //       setTimeout(() => {
      //         if (containerRef.current) {
      //           containerRef.current.style.scrollBehavior = 'smooth';
      //         }
      //       }, 100);
      //     }
      //   }, 100); // 에디터가 완전히 로드된 후 스크롤하기 위해 약간의 지연 추가
      handleGotoTop('auto');
    }

    HackleManager.logCreateStepThreeProductDescriptionView(
      params.id !== 'draft' ? Number(params.id) : undefined,
    );

    document.addEventListener(
      'click',
      handleTransformYouTubeVideoUrlByClick,
      true,
    );
    document.addEventListener(
      'keydown',
      handleTransformYouTubeVideoUrlByEnter,
      true,
    );

    return () => {
      document.removeEventListener(
        'click',
        handleTransformYouTubeVideoUrlByClick,
        true,
      );
      document.removeEventListener(
        'keydown',
        handleTransformYouTubeVideoUrlByEnter,
        true,
      );
    };
  }, []);

  const renderContents = (): JSX.Element => {
    return (
      <QuillContainer
        ref={containerRef}
        $isMobile={isMobile}
        translate="no"
        className="notranslate"
        style={{
          height: `${editorHeight}px`,
        }}
      >
        {isVisibleHeader && (
          <TitleContainer
            title="프립을 소개해 주세요"
            isMobile={isMobile}
            style={{
              position: 'relative',
              height: 'auto',
              padding: isMobile ? '32px 20px 8px' : '32px 40px 8px',
              // position: isMobile ? 'relative' : 'sticky',
              top: 0,
              zIndex: 21,
            }}
          />
        )}

        <ReactQuill
          theme="snow"
          modules={modules}
          // formats={formats}
          ref={quillRef}
          onChange={value => {
            if (value === '<p><br></p>') {
              handleOnChangeProductParamInput('htmlDetailContent', '');
              return;
            }

            // 숨겨진 표 메타데이터 줄(<temporary>)에 갇힌 내용을 문단으로 꺼낸다
            handleOnChangeProductParamInput(
              'htmlDetailContent',
              normalizeTableTemporary(value),
            );
          }}
          onBlur={handleResizeOnBlur}
          onKeyDown={e => {
            // 붙여넣기 감지 (Ctrl+V 또는 Cmd+V)
            if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
              // 붙여넣기 이벤트가 발생하면, 약간의 지연 후 커서 위치 확인
              setTimeout(() => {
                const selection = quillRef.current?.getEditor().getSelection();
                if (selection && containerRef.current && quillRef.current) {
                  const editor = quillRef.current.getEditor();
                  const bounds = editor.getBounds(selection.index);
                  const containerHeight = containerRef.current.clientHeight;

                  // 항상 커서 위치로 스크롤 (조건 없이)
                  containerRef.current.style.scrollBehavior = 'auto';
                  containerRef.current.scrollTop =
                    bounds.top - containerHeight / 2 + bounds.height;

                  // 스크롤 행동 복원
                  setTimeout(() => {
                    if (containerRef.current) {
                      containerRef.current.style.scrollBehavior = 'smooth';
                    }
                  }, 50);
                }
              }, 50); // 붙여넣기는 약간 더 긴 지연 사용
            }
          }}
        />

        <ImageContainer>
          <input
            ref={fileInputRef}
            type="file"
            multiple={false}
            onChange={handleFileChange}
            accept=".jpg, .png, .jpeg, .gif"
          />
        </ImageContainer>
      </QuillContainer>
    );
  };

  /* 에디터 스크롤 감지 */
  const { scroll } = useScrollCheck({
    targetElement: document.querySelector('.quill')?.parentElement,
  });

  return (
    <EditorFormat>
      {imageLoading && <ImageUploadLoading />}
      {renderContents()}

      {scroll && (
        <GoTopButtonBox $isMobile={isMobile}>
          <GoTopButton handleGotoTop={() => handleGotoTop('smooth')} />
        </GoTopButtonBox>
      )}
    </EditorFormat>
  );
};

export default ChapterThree;
